<template>
  <article class="gpc" @click="$emit('view', product)">
    <div class="gpc__img-wrap">
      <img
        :src="product.thumbnailimage || placeholderImage"
        :alt="product.productname"
        class="gpc__img"
        loading="lazy"
        @error="onImgError"
      />
      <span v-if="product.featured === 'YES'" class="gpc__badge gpc__badge--featured">Featured</span>
      <span v-if="inCartQty > 0" class="gpc__badge gpc__badge--cart">{{ inCartQty }} in cart</span>
    </div>
    <div class="gpc__body">
      <p v-if="product.brand" class="gpc__brand">{{ product.brand }}</p>
      <h3 class="gpc__name">{{ product.productname }}</h3>
      <p class="gpc__meta">
        <span v-if="product.weight">{{ product.weight }}</span>
        <span v-if="product.weight && product.unit"> · </span>
        <span v-if="product.unit">{{ product.unit }}</span>
      </p>
      <div class="gpc__footer" @click.stop>
        <div v-if="sellingPrice" class="gpc__pricing">
          <span class="gpc__price">Rs. {{ formatPrice(sellingPrice) }}</span>
          <span v-if="showMrp" class="gpc__mrp">Rs. {{ formatPrice(mrp) }}</span>
          <span v-if="discountPercent" class="gpc__discount">{{ discountPercent }}% off</span>
        </div>
        <div v-if="inCartQty > 0" class="gpc__qty">
          <button type="button" class="gpc__qty-btn" aria-label="Decrease quantity" @click="$emit('decrease', product)">−</button>
          <span class="gpc__qty-val">{{ inCartQty }}</span>
          <button type="button" class="gpc__qty-btn" aria-label="Increase quantity" @click="$emit('increase', product)">+</button>
        </div>
        <button v-else type="button" class="gpc__add" :disabled="adding" @click="$emit('add', product)">
          {{ adding ? 'Adding…' : 'Add' }}
        </button>
      </div>
    </div>
  </article>
</template>

<script>
const PLACEHOLDER = 'data:image/svg+xml,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect fill="#f5f0fa" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6e41a1" font-size="14" font-family="sans-serif">Grocery</text></svg>'
);

export default {
  name: 'GroceryProductCard',
  props: {
    product: { type: Object, required: true },
    cartItems: { type: Array, default: () => [] },
    adding: { type: Boolean, default: false },
  },
  emits: ['add', 'increase', 'decrease', 'view'],
  data() {
    return { placeholderImage: PLACEHOLDER };
  },
  computed: {
    inCartQty() {
      const item = this.cartItems.find((i) => i.ecmsproductid === this.product.ecmsproductid);
      return item ? item.quantity : 0;
    },
    cartItem() {
      return this.cartItems.find((i) => i.ecmsproductid === this.product.ecmsproductid) || null;
    },
    sellingPrice() {
      const fromCart = this.cartItem?.price;
      if (fromCart != null && fromCart !== '') return Number(fromCart);
      const raw = this.product.sellingprice ?? this.product.sellerprice;
      return raw != null && raw !== '' ? Number(raw) : null;
    },
    mrp() {
      const raw = this.product.mrp;
      return raw != null && raw !== '' ? Number(raw) : null;
    },
    showMrp() {
      return this.mrp != null && this.sellingPrice != null && this.mrp > this.sellingPrice;
    },
    discountPercent() {
      if (!this.showMrp) return null;
      return Math.round(((this.mrp - this.sellingPrice) / this.mrp) * 100);
    },
  },
  methods: {
    formatPrice(val) {
      const num = Number(val);
      if (Number.isNaN(num)) return '0';
      return num.toLocaleString('en-NP', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    onImgError(e) {
      e.target.src = this.placeholderImage;
    },
  },
};
</script>

<style scoped>
.gpc {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #ebe3f5;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  height: 100%;
}

.gpc:hover {
  box-shadow: 0 8px 24px rgba(110, 65, 161, 0.12);
  transform: translateY(-2px);
}

.gpc__img-wrap {
  position: relative;
  aspect-ratio: 1;
  background: #f9f7fc;
  overflow: hidden;
}

.gpc__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gpc__badge {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 8px;
  border-radius: 6px;
}

.gpc__badge--featured {
  background: #fef3c7;
  color: #b45309;
}

.gpc__badge--cart {
  background: #6e41a1;
  color: #fff;
  left: auto;
  right: 8px;
}

.gpc__body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
}

.gpc__brand {
  margin: 0;
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.gpc__name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gpc__meta {
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
}

.gpc__footer {
  margin-top: auto;
  padding-top: 10px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
}

.gpc__pricing {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}

.gpc__price {
  font-size: 15px;
  font-weight: 800;
  color: #5a3485;
  line-height: 1.2;
}

.gpc__mrp {
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  text-decoration: line-through;
}

.gpc__discount {
  font-size: 10px;
  font-weight: 700;
  color: #b45309;
  background: #fef3c7;
  padding: 2px 6px;
  border-radius: 4px;
}

.gpc__add {
  background: #6e41a1;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.gpc__add:hover:not(:disabled) {
  background: #5a3485;
}

.gpc__add:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.gpc__qty {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1.5px solid #6e41a1;
  border-radius: 8px;
  overflow: hidden;
}

.gpc__qty-btn {
  background: #f5f0fa;
  border: none;
  width: 30px;
  height: 30px;
  font-size: 16px;
  font-weight: 700;
  color: #6e41a1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gpc__qty-btn:hover {
  background: #ebe3f5;
}

.gpc__qty-val {
  min-width: 28px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #5a3485;
}
</style>
